import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  X,
  Mail,
  MessageSquare,
  ExternalLink,
  ShieldCheck,
  Award,
  Sparkles,
  MapPin,
  Building2,
  UserPlus,
  Check,
  AtSign,
  Briefcase,
  TrendingUp,
  Medal,
  ChevronRight,
  Send
} from 'lucide-react';

export interface UserProfileData {
  id?: string | number;
  name: string;
  role?: string;
  company?: string;
  avatar?: string;
  status?: string;
  email?: string;
  department?: string;
  location?: string;
  bio?: string;
  isOnline?: boolean;
}

export interface UserProfileModalProps {
  user: UserProfileData | null;
  onClose: () => void;
  onNavigateToFullProfile?: (user: UserProfileData) => void;
  onMentionUser?: (user: UserProfileData) => void;
}

export default function UserProfileModal({
  user,
  onClose,
  onNavigateToFullProfile,
  onMentionUser
}: UserProfileModalProps) {
  const [isFollowing, setIsFollowing] = useState(false);
  const [quickMsg, setQuickMsg] = useState('');
  const [msgSent, setMsgSent] = useState(false);

  if (!user) return null;

  const handleSendQuickMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickMsg.trim()) return;
    setMsgSent(true);
    setTimeout(() => {
      setMsgSent(false);
      setQuickMsg('');
    }, 3000);
  };

  const isCurrentUser = user.name.toLowerCase().includes('você') || user.name.toLowerCase() === 'você (usuário)';

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-950/60 backdrop-blur-xs">
        {/* Backdrop click */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0"
        />

        {/* Modal Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.94, y: 12 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="relative z-10 w-full max-w-lg bg-white dark:bg-[#0F172A] border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl overflow-hidden text-slate-900 dark:text-white flex flex-col"
        >
          {/* Executive Top Banner */}
          <div className="h-28 w-full bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 relative overflow-hidden p-4 flex items-start justify-between">
            <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#38bdf8_1px,transparent_1px)] [background-size:12px_12px]" />
            
            <span className="relative z-10 text-[10px] font-mono font-bold tracking-widest uppercase text-indigo-300 bg-indigo-500/20 backdrop-blur-md border border-indigo-400/30 px-2.5 py-1 rounded-full flex items-center gap-1.5">
              <Sparkles size={11} className="text-amber-400" />
              <span>Perfil do Pulse</span>
            </span>

            <button
              type="button"
              onClick={onClose}
              className="relative z-10 w-8 h-8 rounded-full bg-slate-900/60 hover:bg-slate-900 text-slate-300 hover:text-white transition-all flex items-center justify-center cursor-pointer border border-white/10"
              title="Fechar"
            >
              <X size={16} />
            </button>
          </div>

          {/* Profile Header Area */}
          <div className="px-6 pb-6 pt-0 relative flex flex-col gap-5 -mt-12">
            {/* Avatar & Main Info Row */}
            <div className="flex items-end justify-between gap-4">
              <div className="relative group">
                <div className="w-20 h-20 sm:w-22 sm:h-22 rounded-2xl bg-slate-900 border-4 border-white dark:border-[#0F172A] shadow-lg overflow-hidden shrink-0 relative flex items-center justify-center text-white text-xl font-black">
                  {user.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span>{user.name.charAt(0)}</span>
                  )}
                </div>
                <span
                  className={`absolute bottom-1 right-1 w-4 h-4 rounded-full border-2 border-white dark:border-[#0F172A] ${
                    user.isOnline !== false ? 'bg-emerald-500' : 'bg-slate-400'
                  } shadow-xs`}
                  title={user.status || (user.isOnline !== false ? 'Online no Pulse' : 'Ausente')}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 pb-1">
                {!isCurrentUser && (
                  <button
                    type="button"
                    onClick={() => setIsFollowing(!isFollowing)}
                    className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs ${
                      isFollowing
                        ? 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-200'
                        : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-xs'
                    }`}
                  >
                    {isFollowing ? (
                      <>
                        <Check size={14} />
                        <span>Seguindo</span>
                      </>
                    ) : (
                      <>
                        <UserPlus size={14} />
                        <span>Seguir</span>
                      </>
                    )}
                  </button>
                )}

                {onMentionUser && (
                  <button
                    type="button"
                    onClick={() => {
                      onMentionUser(user);
                      onClose();
                    }}
                    className="px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700 text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer"
                    title="Mencionar no Feed"
                  >
                    <AtSign size={14} />
                    <span>Mencionar</span>
                  </button>
                )}
              </div>
            </div>

            {/* Name & Title */}
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl font-display font-bold text-slate-900 dark:text-white leading-tight">
                  {user.name}
                </h2>
                <span className="bg-emerald-50 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200/80 dark:border-emerald-400/30 text-[10px] font-bold px-2 py-0.5 rounded-md flex items-center gap-1">
                  <ShieldCheck size={12} /> Executivo Verificado
                </span>
              </div>

              <p className="text-xs text-slate-600 dark:text-slate-300 font-medium flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-900 dark:text-slate-200 flex items-center gap-1">
                  <Briefcase size={12} className="text-slate-400" />
                  {user.role || 'Membro do Workspace'}
                </span>
                <span className="text-slate-300 dark:text-slate-700">•</span>
                <span className="flex items-center gap-1">
                  <Building2 size={12} className="text-slate-400" />
                  {user.company || user.department || 'Cyzor Tech'}
                </span>
              </p>

              {user.status && (
                <p className="text-xs text-slate-500 dark:text-slate-400 italic mt-0.5">
                  "{user.status}"
                </p>
              )}
            </div>

            {/* Executive Quick Stats */}
            <div className="grid grid-cols-3 gap-2 py-3 px-3.5 bg-slate-50 dark:bg-slate-900/80 border border-slate-200/80 dark:border-slate-800 rounded-2xl">
              <div className="flex flex-col items-center justify-center gap-0.5 text-center">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Posts Pulse</span>
                <span className="text-sm font-display font-bold text-slate-900 dark:text-white">142</span>
              </div>
              <div className="flex flex-col items-center justify-center gap-0.5 text-center border-x border-slate-200/80 dark:border-slate-800">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Score</span>
                <span className="text-sm font-display font-bold text-emerald-600 dark:text-emerald-400">98.4</span>
              </div>
              <div className="flex flex-col items-center justify-center gap-0.5 text-center">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Projetos</span>
                <span className="text-sm font-display font-bold text-slate-900 dark:text-white">8 Ativos</span>
              </div>
            </div>

            {/* Executive Stamps / Badges */}
            <div className="flex flex-col gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1">
                <Medal size={12} className="text-amber-500" />
                Selos de Reconhecimento
              </span>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { label: '🏆 Fundador', color: 'bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-500/30' },
                  { label: '⚡ Tech Lead', color: 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-800 dark:text-indigo-300 border-indigo-200 dark:border-indigo-500/30' },
                  { label: '🎯 Visão 10x', color: 'bg-purple-50 dark:bg-purple-500/10 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-500/30' },
                  { label: '🛡️ ISO 27001', color: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30' }
                ].map((badge, idx) => (
                  <span
                    key={idx}
                    className={`text-[10.5px] font-bold px-2.5 py-1 rounded-lg border ${badge.color}`}
                  >
                    {badge.label}
                  </span>
                ))}
              </div>
            </div>

            {/* Quick Message Input Box */}
            <form onSubmit={handleSendQuickMessage} className="flex flex-col gap-2 pt-1 border-t border-slate-100 dark:border-slate-800">
              <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300 flex items-center justify-between">
                <span>Enviar mensagem rápida:</span>
                {msgSent && (
                  <span className="text-emerald-600 dark:text-emerald-400 text-[10px] font-bold">
                    ✓ Mensagem enviada com sucesso!
                  </span>
                )}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={quickMsg}
                  onChange={e => setQuickMsg(e.target.value)}
                  placeholder={`Escreva algo para ${user.name}...`}
                  className="flex-1 px-3.5 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-slate-900 dark:text-white"
                />
                <button
                  type="submit"
                  disabled={!quickMsg.trim()}
                  className="px-3.5 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl text-xs font-bold hover:bg-slate-800 dark:hover:bg-slate-100 transition-all disabled:opacity-40 cursor-pointer flex items-center gap-1 shrink-0"
                >
                  <Send size={13} />
                  <span>Enviar</span>
                </button>
              </div>
            </form>

            {/* View Full Profile Main Button */}
            <div className="pt-2">
              <button
                type="button"
                onClick={() => {
                  if (onNavigateToFullProfile) {
                    onNavigateToFullProfile(user);
                  }
                  onClose();
                }}
                className="w-full py-3 px-4 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold text-xs hover:bg-slate-800 dark:hover:bg-slate-100 transition-all flex items-center justify-center gap-2 cursor-pointer shadow-md active:scale-98"
              >
                <span>Ver Perfil Completo do Membro</span>
                <ChevronRight size={15} />
              </button>
            </div>

          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
